<template>
  <div class="navbar-container d-flex content align-items-center">

    <!-- Nav Menu Toggler -->
    <ul class="nav navbar-nav d-xl-none">
      <li class="nav-item">
        <b-link
          class="nav-link nav-toggler"
          aria-label="Open menu"
          @click="toggleVerticalMenuActive"
        >
          <feather-icon
            icon="MenuIcon"
            size="21"
          />
        </b-link>
      </li>
    </ul>

    <b-navbar-nav class="nav align-items-center ml-auto nb">
      <li
        v-if="organization"
        class="d-sm-flex d-none align-items-center nb-org"
      >
        <span class="nb-org-name">
          {{ organization.name }}
        </span>
        <feather-icon
          icon="ChevronRightIcon"
          size="16"
          class="nb-org-caret"
        />
      </li>

      <b-nav-item-dropdown
        v-if="location"
        right
        toggle-class="d-flex align-items-center nb-toggle"
        menu-class="nb-menu"
      >
        <template #button-content>
          <span class="sr-only">Switch location</span>
          <feather-icon
            icon="MapPinIcon"
            size="15"
            class="nb-toggle-pin"
          />
          <span class="d-sm-inline d-none nb-location">
            {{ location.name }}
          </span>
          <feather-icon
            icon="ChevronDownIcon"
            size="15"
            class="nb-toggle-caret"
          />
        </template>

        <b-dropdown-item
          v-for="(loc, index) in locations"
          :key="loc.id"
          link-class="d-flex align-items-center nb-item"
          :class="{ 'active': index === currentLocationIndex }"
          @click="switchLocation(index)"
        >
          <feather-icon
            size="15"
            icon="MapPinIcon"
            class="mr-50"
          />
          <span>{{ loc.name }}</span>
        </b-dropdown-item>

      </b-nav-item-dropdown>

      <li class="nb-logout-item">
        <button
          type="button"
          class="nb-logout"
          aria-label="Logout"
          @click="logout"
        >
          <feather-icon
            size="15"
            icon="LogOutIcon"
          />
          <span class="d-sm-inline d-none">Logout</span>
        </button>
      </li>
    </b-navbar-nav>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  BLink, BNavbarNav, BNavItemDropdown, BDropdownItem,
} from 'bootstrap-vue'

export default {
  components: {
    BLink,
    BNavbarNav,
    BNavItemDropdown,
    BDropdownItem,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters({
      location: 'authentication/location',
      locations: 'authentication/locations',
      organization: 'authentication/organization',
    }),
    currentLocationIndex() {
      return this.$store.state.authentication.currentLocationIndex
    },
  },
  methods: {
    logout() {
      this.$store.dispatch('authentication/logout')
      this.$router.push({ name: 'login' })
    },
    switchLocation(index) {
      this.$store.dispatch('authentication/switchLocation', index)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/design-system/mixins';

.nav-toggler {
  color: $c-text-sec;

  &:hover { color: $c-text; }
}

.nb-org {
  gap: 4px;
  margin-right: 6px;
}

.nb-org-name {
  font-size: 12.5px;
  font-weight: 600;
  color: $c-text-sec;
  white-space: nowrap;
}

.nb-org-caret { color: $c-text-ter; }

.nb ::v-deep .nb-toggle {
  flex-wrap: nowrap;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid $c-border;
  border-radius: 10px;
  background: $c-white;
  color: $c-text;

  &::after { display: none; }

  &:hover { background: $c-bg; }
}

.nb-toggle-pin { color: $c-orange; }

.nb-location {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.nb-toggle-caret { color: $c-text-ter; }

.nb ::v-deep .nb-menu {
  max-height: 320px;
  overflow-y: auto;
  // Flush against the toggle: the gap read as a seam. Overrides Bootstrap's own too.
  margin-top: 0;
  padding: 6px;
  border: 1px solid $c-border;
  border-radius: 12px;
  box-shadow: 0 14px 36px rgba($c-wine, 0.14);
}

.nb ::v-deep .nb-item {
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: $c-text;

  svg { color: $c-text-ter; }

  &:hover {
    background: $c-bg;
    color: $c-text;
  }
}

.nb ::v-deep .dropdown-item.active .nb-item,
.nb ::v-deep .active > .nb-item {
  background: $c-wine-light;
  color: $c-wine;
  font-weight: 700;

  svg { color: $c-orange; }
}

.nb-logout-item {
  display: flex;
  align-items: center;
  margin-left: 4px;
  padding-left: 8px;
  border-left: 1px solid $c-border;
}

.nb-logout {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: none;
  color: $c-text-sec;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  svg { color: $c-text-ter; }

  &:hover {
    background: $c-danger-light;
    color: $c-danger-text;

    svg { color: $c-danger-text; }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba($c-orange, 0.28);
  }
}
</style>
